import {
  AlchemyResource,
  APIRetry,
  DiagnosticsActionStatus,
  ExternalApi,
  GraphNode,
  JsonDocument,
  LogEventName,
  LogLevel,
  LogMessageCodes,
  NodeType,
  ParamOrVar,
  RaisedEventState,
  RaisedEventStatus,
  SafDiagnosticsSettings,
  StaticConfig,
  YodaContextVariablesKey
} from '../../models';
import {
  DiagnosticsExecutionStatus,
  DiagnosticsProgressResponse,
  FormField,
  FormFieldV2,
  FormV2,
  SafConstants,
  Timer
} from '../../models/safDiagnostic.models';
import { deserializeToImmutableMap } from '../../serialize';
import {
  combineStates,
  createImmutableList,
  createImmutableMap,
  createStateField,
  IImmutableList,
  IImmutableMap,
  InferReducer
} from '../../state/redux';
import {
  addFormFields,
  checkNodePreCondition,
  delContextVariables,
  generateAdhocs,
  pushPreconditionNodes,
  setContextVariables,
  updateAdhocVariableValues,
  updateRenderedTargetNode,
  uuidOptions
} from '../../utils';
import { populateDiagnosticsForm } from '../../utils/safDiagnosticSchema';
import { Error } from '../../models/app.models';

import {
  abandonDiagnosticsRunAction,
  cancelEditNodeChoiceSelectionAction,
  diagnosticsCompletedAction,
  diagnosticsStartedAction,
  editNodeChoiceSelectionAction,
  errorManagerActionTypes,
  fetchAlchemyResourceAction,
  fetchDialogCompleteAction,
  fetchDialogStartedAction,
  fetchingSafDiagnosticsAction,
  incrementDiagnosticsRunProgressAction,
  initializeAppAction,
  IntentGraphAppActionTypes,
  loadDiagnosticResourceAction,
  NodeActionTypes,
  pushTargetNodeFromInputNodeAction,
  pushTargetNodeOnRenderedNodesAction,
  resetApiRetryCountAction,
  resetDiagnosticsRunStatusAction,
  resetErrorAction,
  resetErrorsAction,
  resetSafDiagnosticAction,
  resetStoreAction,
  safDiagnosticRenderedAction,
  safDiagnosticsCompletedAction,
  setAlchemyResourceAction,
  setContextVarsAction,
  setDiagnosticsRunProgressAction,
  setDiagnosticsStatusAction,
  setErrorAction,
  startDiagnosticsRunAction,
  startDiagnosticsTimerAction,
  stopDiagnosticsTimerAction,
  updateApiRetryCountAction,
  updateDiagnosticsFormComponentAction,
  updateGlobalVarsAction,
  updateInputFormFieldsAction,
  updateInputTextAction,
  updateRaisedEventAction
} from './actions';

const uuidv4 = require('uuid/v4');

const isComponentInitialized = createStateField(false, {
  [IntentGraphAppActionTypes.InitializeApp]: (_: boolean, _action: ReturnType<typeof initializeAppAction>) => {
    return true;
  },
  [IntentGraphAppActionTypes.ResetStore]: (_: boolean, _action: ReturnType<typeof resetStoreAction>) => {
    return false;
  }
});

const isFetchingDialog = createStateField(false, {
  [NodeActionTypes.FetchingDialogStarted]: (_: boolean, _action: ReturnType<typeof fetchDialogStartedAction>) => {
    return true;
  },
  [NodeActionTypes.FetchingDialogComplete]: (_: boolean, _action: ReturnType<typeof fetchDialogCompleteAction>) => {
    return false;
  }
});

const staticConfig = createStateField(createImmutableMap(new StaticConfig()), {
  [IntentGraphAppActionTypes.InitializeApp]: (
    state: IImmutableMap<StaticConfig>,
    action: ReturnType<typeof initializeAppAction>
  ) => {
    let jsonDocument = action.payload.staticConfig.payload
      ? deserializeToImmutableMap(JsonDocument, action.payload.staticConfig.payload)
      : null;
    let meta = jsonDocument.get('meta');
    meta = meta
      .set('parentDialogId', 'root')
      .set('parentDialogName', 'root')
      .set('dialogIdChain', meta.get('id'));
    jsonDocument = jsonDocument.set('meta', meta);
    const safDiagnosticsSettings = action.payload.staticConfig.safDiagnosticsSettings
      ? deserializeToImmutableMap(SafDiagnosticsSettings, action.payload.staticConfig.safDiagnosticsSettings)
      : null;
    return state.withMutations((mutableState: IImmutableMap<StaticConfig>) => {
      mutableState.set('sessionId', action.payload.staticConfig.sessionId);
      mutableState.set('hostAppName', action.payload.staticConfig.hostAppName);
      mutableState.set('locale', action.payload.staticConfig.locale);
      mutableState.set('prefix', action.payload.staticConfig.prefix);
      mutableState.set('timeoutInSeconds', action.payload.staticConfig.timeoutInSeconds);
      mutableState.set('enableScrollToBottom', action.payload.staticConfig.enableScrollToBottom);
      mutableState.set('enableScrollToTop', action.payload.staticConfig.enableScrollToTop);
      mutableState.set('scrollRef', action.payload.staticConfig.scrollRef);
      mutableState.set('correlationId', action.payload.staticConfig.correlationId);
      mutableState.set('safDiagnosticsSettings', safDiagnosticsSettings);
      mutableState.set('disableValidationException', action.payload.staticConfig.disableValidationException);
      mutableState.set('logLinkClicks', action.payload.staticConfig.logLinkClicks);
      mutableState.set('useCallBacksForSAFApis', !!action.payload.staticConfig.useCallBacksForSAFApis);
      mutableState.set('debugRootStore', action.payload.staticConfig.debugRootStore);
      mutableState.set('jsonDocuments', createImmutableList([jsonDocument]));
    });
  },
  [IntentGraphAppActionTypes.ResetStore]: (
    _state: IImmutableMap<StaticConfig>,
    _action: ReturnType<typeof resetStoreAction>
  ) => {
    return createImmutableMap(new StaticConfig());
  },
  [NodeActionTypes.FetchingDialogComplete]: (
    state: IImmutableMap<StaticConfig>,
    action: ReturnType<typeof fetchDialogCompleteAction>
  ) => {
    return state.withMutations((mutableState: IImmutableMap<StaticConfig>) => {
      if (!action.payload.dialogFound) {
        let jsonPayload = action.payload.jsonDocument;
        const parentJsonPayload = action.payload.parentJsonDocument;
        let meta = jsonPayload.get('meta');
        const dialogIdChain = `${parentJsonPayload.get('meta').get('dialogIdChain')};${meta.get('id')}`;
        meta = meta
          .set('parentDialogId', parentJsonPayload.get('meta').get('id'))
          .set('parentDialogName', parentJsonPayload.get('meta').get('name'))
          .set('dialogIdChain', dialogIdChain);
        jsonPayload = jsonPayload.set('meta', meta);
        mutableState.set('jsonDocuments', mutableState.get('jsonDocuments').push(jsonPayload));
      }
    });
  },
  [NodeActionTypes.SafDiagnosticsRendered]: (
    state: IImmutableMap<StaticConfig>,
    action: ReturnType<typeof safDiagnosticRenderedAction>
  ) => {
    return state.withMutations((mutableState: IImmutableMap<StaticConfig>) => {
      let jsonDocuments = mutableState.get('jsonDocuments');
      let updatedJsonDocuments: IImmutableMap<JsonDocument>[] = [];
      jsonDocuments.forEach((jsonDocument: IImmutableMap<JsonDocument>) => {
        let nodes = jsonDocument.get('nodes');
        let updatedNodes: IImmutableMap<GraphNode>[] = [];
        nodes.forEach((n: IImmutableMap<GraphNode>) => {
          if (n.get('id') === action.payload.nodeId) {
            updatedNodes.push(n.set('diagnosticUId', action.payload.diagnosticUId));
          } else {
            updatedNodes.push(n);
          }
        });
        updatedJsonDocuments.push(jsonDocument.set('nodes', createImmutableList(updatedNodes)));
      });
      mutableState.set('jsonDocuments', createImmutableList(updatedJsonDocuments));
    });
  }
});

const renderedNodes = createStateField(createImmutableList([]), {
  [IntentGraphAppActionTypes.InitializeApp]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof initializeAppAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      if (mutableRenderedNodes.count() > 0) {
        mutableRenderedNodes.clear();
      }
      const jsonPayload = deserializeToImmutableMap(JsonDocument, action.payload.staticConfig.payload);
      const startNodeId = jsonPayload.get('startNodeId');
      let startNode = jsonPayload.get('nodes').find((node: IImmutableMap<GraphNode>) => {
        return node.get('id') === startNodeId;
      });
      if (!!startNode) {
        const dialogId = jsonPayload.get('meta').get('id');
        startNode = updateRenderedTargetNode(
          mutableRenderedNodes,
          startNode,
          'root',
          dialogId,
          jsonPayload,
          action.payload.isUserSignedIn
        );
        const nodePreCondition = checkNodePreCondition(
          jsonPayload.get('initialVariables'),
          startNode,
          jsonPayload.get('nodes')
        );
        if (nodePreCondition.preConditionNodes.length > 0) {
          mutableRenderedNodes = pushPreconditionNodes(
            nodePreCondition.preConditionNodes,
            startNode,
            mutableRenderedNodes,
            dialogId
          );
        } else {
          mutableRenderedNodes.push(startNode);
        }
      } else {
        if (!!action.payload.logHandler && typeof action.payload.logHandler === 'function') {
          const sessionId = action.payload.staticConfig.sessionId;
          const metadata = jsonPayload.get('meta');
          const Adhocs = generateAdhocs(metadata);
          action.payload.logHandler(
            LogEventName.onJsonDocumentValidationError,
            LogLevel.Error,
            sessionId,
            LogMessageCodes.StartNodeNotFound,
            Adhocs
          );
        }
      }
    });
  },
  [NodeActionTypes.FetchingDialogComplete]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof fetchDialogCompleteAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      let targetNode: IImmutableMap<GraphNode> | null = null;
      const jsonPayload = action.payload.jsonDocument;
      if (action.payload.failedTargetNode) {
        targetNode = action.payload.failedTargetNode;
      } else {
        const startNodeId = jsonPayload.get('startNodeId');
        targetNode = jsonPayload.get('nodes').find((node: IImmutableMap<GraphNode>) => {
          return node.get('id') === startNodeId;
        });
      }

      if (!!targetNode) {
        const lastRenderedNode = mutableRenderedNodes.last();
        const dialogId = jsonPayload.get('meta').get('id');
        targetNode = updateRenderedTargetNode(
          mutableRenderedNodes,
          targetNode,
          lastRenderedNode.get('uId'),
          dialogId,
          jsonPayload,
          action.payload.isUserSignedIn
        );
        const nodePreCondition = checkNodePreCondition(
          jsonPayload.get('initialVariables'),
          targetNode,
          jsonPayload.get('nodes')
        );
        if (nodePreCondition.preConditionNodes && nodePreCondition.preConditionNodes.length > 0) {
          mutableRenderedNodes = pushPreconditionNodes(
            nodePreCondition.preConditionNodes,
            targetNode,
            mutableRenderedNodes,
            dialogId
          );
        } else {
          mutableRenderedNodes.push(targetNode);
        }
      } else {
        if (!!action.payload.logHandler && typeof action.payload.logHandler === 'function') {
          const sessionId = action.payload.sessionId;
          const metadata = jsonPayload.get('meta');
          const Adhocs = generateAdhocs(metadata);
          action.payload.logHandler(
            LogEventName.onJsonDocumentValidationError,
            LogLevel.Error,
            sessionId,
            LogMessageCodes.StartNodeNotFound,
            Adhocs
          );
        }
      }
    });
  },
  [NodeActionTypes.PushTargetNodeOnRenderedNodes]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof pushTargetNodeOnRenderedNodesAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      let dialogId: string;
      mutableRenderedNodes.updateIn(
        [mutableRenderedNodes.findLastIndex((n: IImmutableMap<GraphNode>) => n.get('uId') === action.payload.uId)],
        (node: IImmutableMap<GraphNode>) => {
          dialogId = node.get('dialogId');
          return node
            .set('selectedChoice', action.payload.selectedChoice)
            .set('isInEditMode', false)
            .set('isFirstRender', false);
        }
      );
      if (!!action.payload.targetNode) {
        let updatedTargetNode = updateRenderedTargetNode(
          mutableRenderedNodes,
          action.payload.targetNode,
          action.payload.uId,
          dialogId
        );
        if (action.payload.preConditionNodes.length > 0) {
          mutableRenderedNodes = pushPreconditionNodes(
            action.payload.preConditionNodes,
            updatedTargetNode,
            mutableRenderedNodes,
            dialogId
          );
        } else {
          mutableRenderedNodes.push(updatedTargetNode);
        }
      }
    });
  },
  [NodeActionTypes.PushTargetNodeFromInputNode]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof pushTargetNodeFromInputNodeAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      let dialogId = action.payload.dialogId;
      if (!!action.payload.targetNode) {
        let updatedTargetNode = updateRenderedTargetNode(
          mutableRenderedNodes,
          action.payload.targetNode,
          action.payload.uId,
          dialogId
        );
        const nodeIndex = mutableRenderedNodes.findIndex(
          (n: IImmutableMap<GraphNode>) => n.get('uId') === action.payload.uId
        );
        const startIndex = nodeIndex + 1;
        mutableRenderedNodes.slice(0, startIndex);
        updatedTargetNode = updatedTargetNode.set('dialogId', dialogId);
        if (action.payload.preConditionNodes.length > 0) {
          mutableRenderedNodes = pushPreconditionNodes(
            action.payload.preConditionNodes,
            updatedTargetNode,
            mutableRenderedNodes,
            dialogId
          );
        } else {
          mutableRenderedNodes.push(updatedTargetNode);
        }
      }
    });
  },
  [NodeActionTypes.SafDiagnosticsRendered]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof safDiagnosticRenderedAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      let dialogId: string;
      let idx = -1;
      mutableRenderedNodes.updateIn(
        [
          mutableRenderedNodes.findIndex((n: IImmutableMap<GraphNode>, index: number) => {
            idx = index;
            return n.get('uId') === action.payload.uId;
          })
        ],
        (node: IImmutableMap<GraphNode>) => {
          dialogId = node.get('dialogId');
          return node.set('isInEditMode', true).set('diagnosticUId', action.payload.diagnosticUId);
        }
      );
      /* only add target node if success/target node has not been added yet(only if last node is the diagnostic node). 
      A race condition will happen if there is an error after render has started
      */
      if (!!action.payload.targetNode && idx === mutableRenderedNodes.size - 1) {
        let updatedTargetNode = action.payload.targetNode.withMutations(
          (mutableTargetNode: IImmutableMap<GraphNode>) => {
            mutableTargetNode.set('isInEditMode', true);
          }
        );
        // remove safNode if rendering failed, to prevent white space
        if (!action.payload.success) {
          mutableRenderedNodes.pop();
        }
        if (!updatedTargetNode.get('uId')) {
          updatedTargetNode = updatedTargetNode.set('uId', `${uuidv4(uuidOptions())}`);
        }
        if (updatedTargetNode.get('type').toLowerCase() === NodeType.InputNode) {
          updatedTargetNode = addFormFields(updatedTargetNode);
        }
        updatedTargetNode = updatedTargetNode.set('parentUId', action.payload.uId);
        updatedTargetNode = updatedTargetNode.set('dialogId', dialogId);
        if (action.payload.preConditionNodes.length > 0) {
          mutableRenderedNodes = pushPreconditionNodes(
            action.payload.preConditionNodes,
            updatedTargetNode,
            mutableRenderedNodes,
            dialogId
          );
        } else {
          mutableRenderedNodes.push(updatedTargetNode);
        }
      }
    });
  },
  [NodeActionTypes.EditNodeChoiceSelection]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof editNodeChoiceSelectionAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      const nodeIndex = mutableRenderedNodes.findIndex(
        (n: IImmutableMap<GraphNode>) => n.get('uId') === action.payload.uId
      );
      const startIndex = nodeIndex + 1;
      mutableRenderedNodes.updateIn([nodeIndex], (n: IImmutableMap<GraphNode>) => {
        return n.set('isInEditMode', true);
      });
      return mutableRenderedNodes.slice(0, startIndex);
    });
  },
  [NodeActionTypes.CancelEditNodeChoiceSelection]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof cancelEditNodeChoiceSelectionAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      let dialogId: string;
      mutableRenderedNodes.updateIn([mutableRenderedNodes.count() - 1], (n: IImmutableMap<GraphNode>) => {
        dialogId = n.get('dialogId');
        return n.set('isInEditMode', false).set('isFirstRender', false);
      });
      action.payload.map((n: IImmutableMap<GraphNode>) => {
        n = n.set('dialogId', dialogId);
        return mutableRenderedNodes.push(n);
      });
    });
  },
  [NodeActionTypes.DiagnosticsActionStarted]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof diagnosticsStartedAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      mutableRenderedNodes.updateIn(
        [mutableRenderedNodes.findIndex((n: IImmutableMap<GraphNode>) => n.get('uId') === action.payload.uId)],
        (n: IImmutableMap<GraphNode>) => {
          return n
            .set('diagnosticsActionStatus', DiagnosticsActionStatus.InProgress)
            .set('timerId', action.payload.timerId);
        }
      );
    });
  },
  [NodeActionTypes.FetchingSafDiagnostics]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof fetchingSafDiagnosticsAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      mutableRenderedNodes.updateIn(
        [mutableRenderedNodes.findIndex((n: IImmutableMap<GraphNode>) => n.get('uId') === action.payload.uId)],
        (n: IImmutableMap<GraphNode>) => {
          const status = action.payload.completed
            ? DiagnosticsActionStatus.FetchingDiagnosticsCompleted
            : DiagnosticsActionStatus.FetchingDiagnosticsInProgress;
          return n.set('diagnosticsActionStatus', status);
        }
      );
    });
  },
  [NodeActionTypes.DiagnosticsActionCompleted]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof diagnosticsCompletedAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      // update the current node to change execution status as well as edit mode
      let dialogId: string;
      mutableRenderedNodes.updateIn(
        [mutableRenderedNodes.findLastIndex((n: IImmutableMap<GraphNode>) => n.get('id') === action.payload.nodeId)],
        (n: IImmutableMap<GraphNode>) => {
          dialogId = n.get('dialogId');
          return n
            .set('diagnosticsActionStatus', action.payload.diagnosticsActionStatus)
            .set('isInEditMode', !!action.payload.isInEditMode)
            .set('isFirstRender', false);
        }
      );

      if (!!action.payload.targetNode) {
        let targetNode = action.payload.targetNode;
        if (!targetNode.get('uId')) {
          targetNode = targetNode.set('uId', `${uuidv4(uuidOptions())}`);
        }
        if (targetNode.get('type').toLowerCase() === NodeType.InputNode) {
          targetNode = addFormFields(targetNode);
        }
        targetNode = targetNode.set('parentUId', action.payload.uId);
        targetNode = targetNode.set('dialogId', dialogId);
        if (action.payload.preConditionNodes.length > 0) {
          mutableRenderedNodes = pushPreconditionNodes(
            action.payload.preConditionNodes,
            targetNode,
            mutableRenderedNodes,
            dialogId
          );
        } else {
          mutableRenderedNodes.push(targetNode);
        }
      }
    });
  },
  [NodeActionTypes.UpdateInputText]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof updateInputTextAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      mutableRenderedNodes.updateIn(
        [mutableRenderedNodes.findLastIndex((n: IImmutableMap<GraphNode>) => n.get('uId') === action.payload.uId)],
        (n: IImmutableMap<GraphNode>) => {
          const formFields = n.get('InputFormField');
          return n.set('InputFormField', formFields.set('Value', action.payload.text || ''));
        }
      );
    });
  },
  [NodeActionTypes.UpdateInputFormFields]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof updateInputFormFieldsAction>
  ) => {
    return state.withMutations((mutableRenderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => {
      mutableRenderedNodes.updateIn(
        [mutableRenderedNodes.findLastIndex((n: IImmutableMap<GraphNode>) => n.get('uId') === action.payload.uId)],
        (n: IImmutableMap<GraphNode>) => {
          return n.set('InputFormField', action.payload.inputFormFields);
        }
      );
    });
  },
  [IntentGraphAppActionTypes.ResetStore]: (
    _state: IImmutableList<IImmutableMap<GraphNode>>,
    _action: ReturnType<typeof resetStoreAction>
  ) => {
    return createImmutableList([]);
  }
});

const prevChoiceSelections = createStateField(createImmutableList([]), {
  [NodeActionTypes.EditNodeChoiceSelection]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    action: ReturnType<typeof editNodeChoiceSelectionAction>
  ) => {
    return state.withMutations((mutablePrevChoiceSelection: IImmutableList<IImmutableMap<GraphNode>>) => {
      mutablePrevChoiceSelection.clear();
      mutablePrevChoiceSelection.merge(action.payload.prevChoiceSelections);
    });
  },
  [NodeActionTypes.CancelEditNodeChoiceSelection]: (
    state: IImmutableList<IImmutableMap<GraphNode>>,
    _action: ReturnType<typeof cancelEditNodeChoiceSelectionAction>
  ) => {
    return state.withMutations((mutablePrevChoiceSelection: IImmutableList<IImmutableMap<GraphNode>>) => {
      return mutablePrevChoiceSelection;
    });
  },
  [IntentGraphAppActionTypes.ResetStore]: (
    _state: IImmutableList<IImmutableMap<GraphNode>>,
    _action: ReturnType<typeof resetStoreAction>
  ) => {
    return createImmutableList([]);
  }
});

const isDiagnosticsActionInProgress = createStateField(false, {
  [NodeActionTypes.DiagnosticsActionStarted]: (
    _state: boolean,
    _action: ReturnType<typeof diagnosticsStartedAction>
  ) => {
    return true;
  },
  [NodeActionTypes.DiagnosticsActionCompleted]: (
    _state: boolean,
    _action: ReturnType<typeof diagnosticsCompletedAction>
  ) => {
    return false;
  },
  [NodeActionTypes.SafDiagnosticsCompleted]: (
    _state: boolean,
    _action: ReturnType<typeof safDiagnosticsCompletedAction>
  ) => {
    return false;
  },
  [IntentGraphAppActionTypes.ResetStore]: (_state: boolean, _action: ReturnType<typeof resetStoreAction>) => {
    return false;
  }
});

const diagnosticsFormModel = createStateField(createImmutableMap(new FormV2()), {
  [NodeActionTypes.LoadDiagnosticResource]: (
    state: IImmutableMap<FormV2>,
    action: ReturnType<typeof loadDiagnosticResourceAction>
  ) => {
    return state.withMutations((mutableForm: IImmutableMap<FormV2>) => {
      const diagnostic = action.payload.diagnostic;
      const jsonSchemaForm = action.payload.jsonSchemaForm;
      if (diagnostic || jsonSchemaForm) {
        mutableForm = populateDiagnosticsForm(diagnostic, mutableForm, true, jsonSchemaForm);
      }
    });
  },
  [NodeActionTypes.SetDiagnosticsRunProgress]: (
    state: IImmutableMap<FormV2>,
    action: ReturnType<typeof setDiagnosticsRunProgressAction>
  ) => {
    return state.withMutations((mutableForm: IImmutableMap<FormV2>) => {
      const response = action.payload;
      if (
        !!response &&
        response.get('Status') === DiagnosticsExecutionStatus.ContinueWithNext &&
        !!response.get('Next') &&
        response.get('Next').size > 0
      ) {
        // createFormField
        const diagnostics = response.get('Next').first();
        mutableForm = populateDiagnosticsForm(diagnostics, mutableForm);
      }
    });
  },
  [NodeActionTypes.ResetSafDiagnostic]: (
    _: IImmutableMap<FormV2>,
    _action: ReturnType<typeof resetSafDiagnosticAction>
  ) => {
    return createImmutableMap(new FormV2());
  },
  [NodeActionTypes.UpdateDiagnosticsFormComponent]: (
    diagnosticsForm: IImmutableMap<FormV2>,
    action: ReturnType<typeof updateDiagnosticsFormComponentAction>
  ) => {
    return diagnosticsForm.withMutations((mutableForm: IImmutableMap<FormV2>) => {
      mutableForm.update('FormFields', (formFields: IImmutableList<IImmutableMap<FormFieldV2>>) => {
        return formFields.updateIn(
          [
            formFields.findIndex((field: IImmutableMap<FormFieldV2>) => {
              return field.get('Key') === action.payload.componentKey;
            })
          ],
          (model: IImmutableMap<FormField>) => {
            return model.set('Value', action.payload.updatedModel);
          }
        );
      });
      let jsonFormValues = mutableForm.get('JsonFormValues');
      jsonFormValues = jsonFormValues.set(action.payload.componentKey, action.payload.updatedModel);
      mutableForm.set('JsonFormValues', jsonFormValues);
    });
  },
  [IntentGraphAppActionTypes.ResetStore]: (_: IImmutableMap<FormV2>, _action: ReturnType<typeof resetStoreAction>) => {
    return createImmutableMap(new FormV2());
  }
});

const diagnosticsResponse = createStateField(createImmutableMap(new DiagnosticsProgressResponse()), {
  [NodeActionTypes.StartDiagnosticsRun]: (
    _: IImmutableMap<DiagnosticsProgressResponse>,
    action: ReturnType<typeof startDiagnosticsRunAction>
  ) => {
    return action.payload;
  },
  [NodeActionTypes.ResetDiagnosticsRunStatus]: (
    _: IImmutableMap<DiagnosticsProgressResponse>,
    _action: ReturnType<typeof resetDiagnosticsRunStatusAction>
  ) => {
    return createImmutableMap(new DiagnosticsProgressResponse());
  },
  [NodeActionTypes.SetDiagnosticsRunProgress]: (
    runStatus: IImmutableMap<DiagnosticsProgressResponse>,
    action: ReturnType<typeof setDiagnosticsRunProgressAction>
  ) => {
    if (!!action.payload) {
      return action.payload;
    }
    return runStatus;
  },
  [NodeActionTypes.SetDiagnosticsStatus]: (
    runStatus: IImmutableMap<DiagnosticsProgressResponse>,
    action: ReturnType<typeof setDiagnosticsStatusAction>
  ) => {
    return runStatus.withMutations((mutableStatus: IImmutableMap<DiagnosticsProgressResponse>) => {
      mutableStatus.set('Status', action.payload.status);
    });
  },
  [NodeActionTypes.AbandonDiagnosticsRun]: (
    runStatus: IImmutableMap<DiagnosticsProgressResponse>,
    action: ReturnType<typeof abandonDiagnosticsRunAction>
  ) => {
    return runStatus.withMutations((mutableStatus: IImmutableMap<DiagnosticsProgressResponse>) => {
      mutableStatus.set('AbandonCurrentRun', action.payload);
    });
  },
  [NodeActionTypes.ResetSafDiagnostic]: (
    _: IImmutableMap<DiagnosticsProgressResponse>,
    _action: ReturnType<typeof resetSafDiagnosticAction>
  ) => {
    return createImmutableMap(new DiagnosticsProgressResponse());
  },
  [IntentGraphAppActionTypes.ResetStore]: (
    _: IImmutableMap<DiagnosticsProgressResponse>,
    _action: ReturnType<typeof resetStoreAction>
  ) => {
    return createImmutableMap(new DiagnosticsProgressResponse());
  }
});

const diagnosticsProgress = createStateField(0, {
  [NodeActionTypes.StartDiagnosticsRun]: (_: number, _action: ReturnType<typeof startDiagnosticsRunAction>) => {
    return 0;
  },
  [NodeActionTypes.SetDiagnosticsStatus]: (_: number, action: ReturnType<typeof setDiagnosticsStatusAction>) => {
    if (action.payload.isTimeout) {
      return SafConstants.InlineDiagnosticsMaxRetriesCount;
    }
    return 0;
  },
  [NodeActionTypes.ResetDiagnosticsRunStatus]: (
    _: number,
    _action: ReturnType<typeof resetDiagnosticsRunStatusAction>
  ) => {
    return 0;
  },
  [NodeActionTypes.ResetSafDiagnostic]: (_: number, _action: ReturnType<typeof resetSafDiagnosticAction>) => {
    return 0;
  },
  [NodeActionTypes.IncrementDiagnosticsRunProgress]: (
    state: number,
    _action: ReturnType<typeof incrementDiagnosticsRunProgressAction>
  ) => {
    return (state += 1);
  },
  [IntentGraphAppActionTypes.ResetStore]: (_: number, _action: ReturnType<typeof resetStoreAction>) => {
    return 0;
  }
});

const globalVars = createStateField(createImmutableList([]), {
  [IntentGraphAppActionTypes.InitializeApp]: (
    state: IImmutableList<IImmutableMap<ParamOrVar>>,
    action: ReturnType<typeof initializeAppAction>
  ) => {
    return state.withMutations((mutableGlobalVars: IImmutableList<IImmutableMap<ParamOrVar>>) => {
      mutableGlobalVars.clear();
      const payload = deserializeToImmutableMap(JsonDocument, action.payload.staticConfig.payload);
      const initialVariables = payload.get('initialVariables');
      const jsonPayload = deserializeToImmutableMap(JsonDocument, action.payload.staticConfig.payload);
      const startNodeId = jsonPayload.get('startNodeId');
      mutableGlobalVars = mutableGlobalVars.merge(initialVariables);
      let startNode = jsonPayload.get('nodes').find((node: IImmutableMap<GraphNode>) => {
        return node.get('id') === startNodeId;
      });
      if (startNode && startNode.get('adhocVariables')) {
        let adhocVariables = startNode.get('adhocVariables');
        const newAdhocVars = updateAdhocVariableValues({ adhocVariables, globalVars: mutableGlobalVars });
        if (newAdhocVars) {
          let yodaContextVariables = createImmutableMap({
            name: YodaContextVariablesKey,
            value: {} as any
          });
          yodaContextVariables = setContextVariables(newAdhocVars, yodaContextVariables, 0);
          mutableGlobalVars.push(yodaContextVariables);
        }
      }
    });
  },
  [IntentGraphAppActionTypes.UpdateGlobalVars]: (
    state: IImmutableList<IImmutableMap<ParamOrVar>>,
    action: ReturnType<typeof updateGlobalVarsAction>
  ) => {
    return state.withMutations((mutableState: IImmutableList<IImmutableMap<ParamOrVar>>) => {
      action.payload.map((nVar: IImmutableMap<ParamOrVar>) => {
        const gVar = mutableState.find((g: IImmutableMap<ParamOrVar>) => {
          return nVar.get('name') && g.get('name') && g.get('name').toLowerCase() === nVar.get('name').toLowerCase();
        });
        if (!!gVar && gVar.size > 0) {
          mutableState.updateIn(
            [
              mutableState.findIndex(
                (s: IImmutableMap<ParamOrVar>) =>
                  nVar.get('name') && s.get('name') && s.get('name').toLowerCase() === nVar.get('name').toLowerCase()
              )
            ],
            (g: IImmutableMap<ParamOrVar>) => g.set('value', nVar.get('value'))
          );
        } else {
          mutableState.push(nVar);
        }
      });
    });
  },
  [NodeActionTypes.PushTargetNodeOnRenderedNodes]: (
    state: IImmutableList<IImmutableMap<ParamOrVar>>,
    action: ReturnType<typeof pushTargetNodeOnRenderedNodesAction>
  ) => {
    return state.withMutations((mutableState: IImmutableList<IImmutableMap<ParamOrVar>>) => {
      if (action.payload.isDynamicChoice) {
        const dynamicChoiceVar = createImmutableMap({
          name: 'dynamicChoice',
          value: action.payload.selectedChoice.get('text').get('content')
        });
        const dynamicChoicePositionVar = createImmutableMap({
          name: 'dynamicChoicePosition',
          value: action.payload.choiceIndex
        });
        const newVars = createImmutableList([dynamicChoiceVar, dynamicChoicePositionVar]);

        newVars.map((nVar: IImmutableMap<ParamOrVar>) => {
          const gVar = mutableState.find((g: IImmutableMap<ParamOrVar>) => {
            return g.get('name') === nVar.get('name');
          });
          if (!!gVar && gVar.size > 0) {
            mutableState.updateIn(
              [
                mutableState.findIndex(
                  (s: IImmutableMap<ParamOrVar>) =>
                    nVar.get('name') && s.get('name') && s.get('name').toLowerCase() === nVar.get('name').toLowerCase()
                )
              ],
              (g: IImmutableMap<ParamOrVar>) => g.set('value', nVar.get('value'))
            );
          } else {
            mutableState.push(nVar);
          }
        });
      }
    });
  },
  [NodeActionTypes.SetContextVars]: (
    state: IImmutableList<IImmutableMap<ParamOrVar>>,
    action: ReturnType<typeof setContextVarsAction>
  ) => {
    return state.withMutations((mutatedstate: IImmutableList<IImmutableMap<ParamOrVar>>) => {
      const adhocVariables = action.payload.adhocVariables;
      let idx = -1;
      if (adhocVariables) {
        let yodaContextVariables = mutatedstate.find((globalVar: IImmutableMap<ParamOrVar>, index: number) => {
          if (globalVar.get('name') === YodaContextVariablesKey) {
            idx = index;
            return true;
          }
          return false;
        });
        if (!yodaContextVariables) {
          yodaContextVariables = createImmutableMap({
            name: YodaContextVariablesKey,
            value: {} as any
          });
        }
        yodaContextVariables = setContextVariables(adhocVariables, yodaContextVariables, action.payload.nodeIndex);
        if (idx === -1) {
          mutatedstate.push(yodaContextVariables);
        } else {
          mutatedstate.set(idx, yodaContextVariables);
        }
      }
    });
  },
  [NodeActionTypes.EditNodeChoiceSelection]: (
    state: IImmutableList<IImmutableMap<ParamOrVar>>,
    action: ReturnType<typeof editNodeChoiceSelectionAction>
  ) => {
    return state.withMutations((mutatedstate: IImmutableList<IImmutableMap<ParamOrVar>>) => {
      let index = -1;
      let yodaContextVariables = mutatedstate.find((globalVar: IImmutableMap<ParamOrVar>, idx: number) => {
        if (globalVar.get('name') === YodaContextVariablesKey) {
          index = idx;
          return true;
        }
        return false;
      });
      if (yodaContextVariables) {
        const updatedYodaContextVariables = delContextVariables(yodaContextVariables, action.payload.nodeIndex);
        mutatedstate.set(index, updatedYodaContextVariables);
      }
    });
  },
  [IntentGraphAppActionTypes.ResetStore]: (
    _: IImmutableList<IImmutableMap<ParamOrVar>>,
    _action: ReturnType<typeof resetStoreAction>
  ) => {
    return createImmutableList([]);
  }
});

/**
 * This reducer has been added to provided cleaner user experience for events such as sign in
 * Some SAF action nodes depend on the user being signed in, if the sign in fails there is no need to show the SAF logic in that case
 */
const raisedEvents = createStateField(createImmutableList([]), {
  [NodeActionTypes.UpdateRaisedEvent]: (
    state: IImmutableList<IImmutableMap<RaisedEventState>>,
    action: ReturnType<typeof updateRaisedEventAction>
  ) => {
    return state.withMutations((mutableState: IImmutableList<IImmutableMap<RaisedEventState>>) => {
      let nodeIdx = -1;
      let uId = action.payload.uId;
      let status = action.payload.status;
      let eventName = action.payload.eventName;
      const updatedRaisedEvent = createImmutableMap({
        raisedBy: uId,
        eventName: eventName,
        status: status
      }) as IImmutableMap<RaisedEventState>;
      if (mutableState.size > 0 && eventName && status && uId) {
        mutableState.updateIn(
          [
            mutableState.findIndex((r: IImmutableMap<RaisedEventState>, idx: number) => {
              let eventFound =
                r &&
                r.get('raisedBy') === uId &&
                r.get('eventName') &&
                r.get('eventName').toLowerCase() === eventName.toLowerCase();
              if (eventFound) {
                nodeIdx = idx;
              }
              return eventFound;
            })
          ],
          (raisedEvent: IImmutableMap<RaisedEventState>) => {
            // there is a race condition with host finishing event before progress is set
            // we will ignore inprogress if host finished first
            if (status !== RaisedEventStatus.inProgress) {
              return raisedEvent.set('status', status);
            }
            return raisedEvent;
          }
        );
      }
      if (nodeIdx === -1) {
        mutableState.push(updatedRaisedEvent);
      }
    });
  },
  [NodeActionTypes.EditNodeChoiceSelection]: (
    state: IImmutableList<IImmutableMap<RaisedEventState>>,
    action: ReturnType<typeof editNodeChoiceSelectionAction>
  ) => {
    return state.withMutations((mutableState: IImmutableList<IImmutableMap<RaisedEventState>>) => {
      let nodeIdx = -1;
      let uId = action.payload.uId;
      mutableState.find((r: IImmutableMap<RaisedEventState>, index: number) => {
        if (r && r.get('raisedBy') === uId) {
          nodeIdx = index;
          return true;
        }
        return false;
      });
      if (nodeIdx != -1) {
        mutableState.remove(nodeIdx);
      }
    });
  }
});

const diagnosticsTimer = createStateField(
  createImmutableMap(
    new Timer(
      SafConstants.InlineDiagnosticsServiceTimerDelayInMilliseconds,
      SafConstants.InlineDiagnosticsMaxRetriesCount
    )
  ),
  {
    [IntentGraphAppActionTypes.InitializeApp]: (
      _state: IImmutableMap<Timer>,
      action: ReturnType<typeof initializeAppAction>
    ) => {
      const staticConfig = action.payload.staticConfig;
      const safSettings = staticConfig.safDiagnosticsSettings;
      const retries =
        safSettings && safSettings.safActionMaxRetries
          ? safSettings.safActionMaxRetries
          : SafConstants.InlineDiagnosticsMaxRetriesCount;
      const delay =
        safSettings && safSettings.serviceTimerDelayInMilliseconds
          ? safSettings.serviceTimerDelayInMilliseconds
          : SafConstants.InlineDiagnosticsServiceTimerDelayInMilliseconds;
      return createImmutableMap(new Timer(delay, retries));
    },
    [NodeActionTypes.StartDiagnosticsTimer]: (
      state: IImmutableMap<Timer>,
      action: ReturnType<typeof startDiagnosticsTimerAction>
    ) => {
      return state.withMutations((mutableTimer: IImmutableMap<Timer>) => {
        mutableTimer.set('IntervalId', action.payload);
      });
    },
    [NodeActionTypes.StopDiagnosticsTimer]: (
      state: IImmutableMap<Timer>,
      _: ReturnType<typeof stopDiagnosticsTimerAction>
    ) => {
      return state.withMutations((mutableTimer: IImmutableMap<Timer>) => {
        mutableTimer.set('IntervalId', undefined);
      });
    },
    [IntentGraphAppActionTypes.ResetStore]: (_: IImmutableMap<Timer>, _action: ReturnType<typeof resetStoreAction>) => {
      return createImmutableMap(
        new Timer(
          SafConstants.InlineDiagnosticsServiceTimerDelayInMilliseconds,
          SafConstants.InlineDiagnosticsMaxRetriesCount
        )
      );
    }
  }
);

const apiRetries = createStateField(createImmutableList([]), {
  [IntentGraphAppActionTypes.InitializeApp]: (
    _state: IImmutableList<IImmutableMap<APIRetry>>,
    action: ReturnType<typeof initializeAppAction>
  ) => {
    const validApiNames = Object.values(ExternalApi);
    const retries: IImmutableMap<APIRetry>[] = validApiNames.map((name: ExternalApi) => {
      const retry = new APIRetry();
      retry.name = name;

      if (action.payload.staticConfig.apiRetries) {
        const customRetry = action.payload.staticConfig.apiRetries.find(
          (r: APIRetry) => r.name && r.name.toLowerCase() === name.toLowerCase()
        );
        if (customRetry) {
          retry.maxRetriesCount =
            customRetry.maxRetriesCount !== undefined ? customRetry.maxRetriesCount : retry.maxRetriesCount;
          retry.enabled = !!customRetry.enabled;
        }
      }
      return createImmutableMap(retry);
    });
    return createImmutableList(retries);
  },
  [NodeActionTypes.UpdateApiRetryCount]: (
    state: IImmutableList<IImmutableMap<APIRetry>>,
    action: ReturnType<typeof updateApiRetryCountAction>
  ) => {
    return state.withMutations((mutableState: IImmutableList<IImmutableMap<APIRetry>>) => {
      mutableState.updateIn(
        [mutableState.findLastIndex((r: IImmutableMap<APIRetry>) => r.get('name') === action.payload.name)],
        (retry: IImmutableMap<APIRetry>) => {
          return retry.set('retriesCount', action.payload.retryCount);
        }
      );
    });
  },
  [NodeActionTypes.ResetApiRetryCount]: (
    state: IImmutableList<IImmutableMap<APIRetry>>,
    action: ReturnType<typeof resetApiRetryCountAction>
  ) => {
    return state.withMutations((mutableState: IImmutableList<IImmutableMap<APIRetry>>) => {
      mutableState.updateIn(
        [mutableState.findLastIndex((r: IImmutableMap<APIRetry>) => r.get('name') === action.payload.name)],
        (retry: IImmutableMap<APIRetry>) => {
          return retry.set('retriesCount', 0);
        }
      );
    });
  },
  [IntentGraphAppActionTypes.ResetStore]: (
    _state: IImmutableList<IImmutableMap<APIRetry>>,
    _action: ReturnType<typeof resetStoreAction>
  ) => {
    return createImmutableList([]);
  }
});

const errorMap = createStateField(createImmutableList([]), {
  [errorManagerActionTypes.SetError]: (
    state: IImmutableList<IImmutableMap<Error>>,
    action: ReturnType<typeof setErrorAction>
  ) => {
    return state.withMutations((mutableErrors: IImmutableList<IImmutableMap<Error>>) => {
      let index = -1;
      const updatedError = mutableErrors.find((error: IImmutableMap<Error>, key: number) => {
        index = key;
        return error.get('ComponentKey') === action.payload.componentKey;
      });
      if (updatedError) {
        updatedError.set('ErrorCode', action.payload.error.get('ErrorCode'));
        mutableErrors.set(index, updatedError);
      } else {
        mutableErrors.push(action.payload.error);
      }
    });
  },
  [errorManagerActionTypes.ResetErrors]: (
    state: IImmutableList<IImmutableMap<Error>>,
    action: ReturnType<typeof resetErrorsAction>
  ) => {
    return state.withMutations((mutableErrors: IImmutableList<IImmutableMap<Error>>) => {
      if (action.payload && action.payload.errorCodes.length > 0) {
        action.payload.errorCodes.forEach((errorCode: string) => {
          let index = -1;
          const updatedErrorMap = mutableErrors.find((error: IImmutableMap<Error>, key: number) => {
            index = key;
            return error.get('ComponentKey') === action.payload.componentKey && error.get('ErrorCode') === errorCode;
          });
          if (updatedErrorMap) {
            mutableErrors.delete(index);
          }
        });
      }
    });
  },
  [errorManagerActionTypes.ResetError]: (
    state: IImmutableList<IImmutableMap<Error>>,
    action: ReturnType<typeof resetErrorAction>
  ) => {
    return state.withMutations((mutableErrors: IImmutableList<IImmutableMap<Error>>) => {
      if (action.payload && action.payload.componentKey) {
        let index = -1;
        const updatedErrorMap = mutableErrors.find((error: IImmutableMap<Error>, key: number) => {
          index = key;
          return error.get('ComponentKey') === action.payload.componentKey;
        });
        if (updatedErrorMap) {
          mutableErrors.delete(index);
        }
      }
    });
  }
});

const resources = createStateField(createImmutableList([]), {
  [NodeActionTypes.FetchAlchemyResource]: (
    state: IImmutableList<IImmutableMap<AlchemyResource>>,
    action: ReturnType<typeof fetchAlchemyResourceAction>
  ) => {
    return state.withMutations((mutableResources: IImmutableList<IImmutableMap<AlchemyResource>>) => {
      if (action.payload.contentId) {
        const resource = new AlchemyResource();
        resource.contentId = action.payload.contentId;
        resource.contentType = action.payload.contentType;
        resource.isDynamic = action.payload.isDynamic;
        resource.isLoading = true;
        mutableResources.push(createImmutableMap(resource));
      }
    });
  },
  [NodeActionTypes.SetAlchemyResource]: (
    state: IImmutableList<IImmutableMap<AlchemyResource>>,
    action: ReturnType<typeof setAlchemyResourceAction>
  ) => {
    return state.withMutations((mutableResources: IImmutableList<IImmutableMap<AlchemyResource>>) => {
      if (action.payload.response && action.payload.contentId) {
        let index = -1
        let resource = mutableResources.find((r: IImmutableMap<AlchemyResource>, idx: number) => {
          if(r.get('contentId') === action.payload.contentId){
            index = idx;
            return true;
          }
          return false;
        })
        if(resource){
          resource = resource.set('solutionContent',action.payload.response.SolutionContent).
                              set('solutionTitle', action.payload.response.SolutionTitle)
                              .set('isLoading', false);
          mutableResources = mutableResources.set(index, resource);
        }
        else{
          let r = new AlchemyResource()
          r.contentId = action.payload.contentId;
          r.solutionContent = action.payload.response.SolutionContent;
          r.solutionTitle = action.payload.response.SolutionTitle;
          mutableResources.push(createImmutableMap(r));
        }
      }
    });
  }
});

type IntentGraphAppProps = {
  isComponentInitialized: InferReducer<typeof isComponentInitialized>;
  staticConfig: InferReducer<typeof staticConfig>;
  isFetchingDialog: InferReducer<typeof isFetchingDialog>;
  renderedNodes: InferReducer<typeof renderedNodes>;
  prevChoiceSelections: InferReducer<typeof prevChoiceSelections>;
  isDiagnosticsActionInProgress: InferReducer<typeof isDiagnosticsActionInProgress>;
  globalVars: InferReducer<typeof globalVars>;
  diagnosticsFormModel: InferReducer<typeof diagnosticsFormModel>;
  diagnosticsProgress: InferReducer<typeof diagnosticsProgress>;
  diagnosticsResponse: InferReducer<typeof diagnosticsResponse>;
  diagnosticsTimer: InferReducer<typeof diagnosticsTimer>;
  raisedEvents: InferReducer<typeof raisedEvents>;
  apiRetries: InferReducer<typeof apiRetries>;
  errorMap: InferReducer<typeof errorMap>;
  resources: InferReducer<typeof resources>;
};
export type IntentGraphAppStates = IImmutableMap<IntentGraphAppProps>;
export const intentGraphAppReducer = combineStates<IntentGraphAppProps>({
  isComponentInitialized,
  isFetchingDialog,
  staticConfig,
  renderedNodes,
  prevChoiceSelections,
  isDiagnosticsActionInProgress,
  globalVars: globalVars,
  diagnosticsFormModel,
  diagnosticsProgress,
  diagnosticsResponse,
  diagnosticsTimer,
  raisedEvents,
  apiRetries,
  errorMap,
  resources
});
